/**
 * GuideOnPage — right rail showing the current section's sub-headings as
 * an "on-this-page" mini-TOC, plus a Related section. Pulled from each
 * section's searchIndex (anchor-bearing entries become the TOC items).
 */
import React from 'react';
import { Box, Typography, List, ListItemButton, ListItemText, Divider } from '@mui/material';
import { GuideSection } from './sections/types';
import { useAppTheme } from '../../hooks/useAppTheme';

interface Props {
  section: GuideSection;
  onAnchor: (anchor: string) => void;
}

const GuideOnPage: React.FC<Props> = ({ section, onAnchor }) => {
  const { isDark } = useAppTheme();
  const tocItems = (section.searchIndex || []).filter(e => !!e.anchor);

  if (tocItems.length === 0) {
    return (
      <Box className="app-guide-onpage" sx={{
        width: 220, flexShrink: 0,
        display: { xs: 'none', lg: 'block' },
        position: 'sticky', top: 110, alignSelf: 'flex-start',
        py: 3,
        color: 'text.secondary', fontSize: '0.78rem',
      }}>
        <Typography sx={{ fontSize: '0.66rem', fontWeight: 700, textTransform: 'uppercase', letterSpacing: 0.6, color: 'text.secondary', mb: 0.5 }}>
          On this page
        </Typography>
        <Typography sx={{ fontSize: '0.75rem', color: 'text.disabled' }}>
          Sub-headings appear here once the section is written.
        </Typography>
      </Box>
    );
  }

  return (
    <Box
      className="app-guide-onpage"
      sx={{
        width: 220, flexShrink: 0,
        display: { xs: 'none', lg: 'block' },
        position: 'sticky', top: 110, alignSelf: 'flex-start',
        py: 3,
        borderLeft: '1px solid', borderColor: 'divider',
        pl: 2,
        maxHeight: 'calc(100vh - 120px)', overflowY: 'auto',
      }}
    >
      <Typography sx={{ fontSize: '0.66rem', fontWeight: 700, textTransform: 'uppercase', letterSpacing: 0.6, color: 'text.secondary', mb: 0.5 }}>
        On this page
      </Typography>
      <List dense disablePadding>
        {tocItems.map((e) => (
          <ListItemButton
            key={e.anchor}
            onClick={() => onAnchor(e.anchor!)}
            sx={{
              py: 0.25,
              borderLeft: '2px solid transparent',
              '&:hover': { borderLeftColor: isDark ? 'rgba(255,255,255,0.3)' : 'rgba(0,0,0,0.25)' },
            }}
          >
            <ListItemText
              primary={e.heading}
              primaryTypographyProps={{ fontSize: '0.78rem' }}
            />
          </ListItemButton>
        ))}
      </List>
      <Divider sx={{ my: 1.5 }} />
      <Typography sx={{ fontSize: '0.7rem', color: 'text.disabled' }}>
        Use the search at the top to jump across sections.
      </Typography>
    </Box>
  );
};

export default GuideOnPage;
